import {
  assertDependenciesResolved,
  concreteVersion,
  findUnrequestedPrereleases,
  transformFile,
} from '@immediately-run/transpiler';

import { UNSUPPORTED_BUILTIN_MODULE_PATH } from '../shims';
import * as logger from '../../utils/logger';
import { sortObj } from '../../utils/object';
import { Bundler } from '../bundler';
import { Module } from '../module/Module';
import { ModuleNotFoundError } from '../../errors/ModuleNotFound';
import { filterBuildDeps } from './build-dep';
import { depMapsEchoMatch, locksetClosureValid, LocksetEchoContext, LocksetSection } from './lockset';
import { ICDNModule, ICDNModuleFile, IResolvedDependency, fetchManifest, fetchModule } from './module-cdn';
import { bundledIndexPath, bundledPackagePath, decodeBundledModule, parseBundledIndex } from './bundledPackages';
import { SELF_HOST_BASES } from '../moduleOrigins';
import {
  EsmFallbackFetcher,
  EsmFallbackSubpathLoader,
  esmFallbackEntryUrl,
  esmFallbackModulePath,
  esmFallbackSubpathPath,
  fetchEsmFallbackSource,
  nativeEsmFallbackFetcher,
  nativeEsmFallbackSubpathLoader,
  synthesizeEsmFallbackModule,
} from './esm-fallback';
import { NodeModule } from './NodeModule';
import { collectUndeclaredImports, UNDECLARED_IMPORT_RESCUE_BUDGET, UndeclaredImport } from './undeclared-transitive';
import { scanCjsModule } from '../transforms/raw-cjs/scan';

// dependency => version range
export type DepMap = { [depName: string]: string };

// The resolution-completeness guard (`assertDependenciesResolved`) lives in
// @immediately-run/transpiler alongside `computeInputDepMap`, the single source
// of truth shared with the CLI cache-zip builder (PRETRANSPILED_ARTIFACTS_SPEC
// §4.4) so a silently-dropped package reads identically at boot and at build time.

export class ModuleRegistry {
  modules: Map<string, NodeModule> = new Map();
  moduleDownloadPromises: Map<string, Promise<NodeModule>> = new Map();

  manifest: IResolvedDependency[] = [];

  // Bundled-package index (R3-49a): `name@version` → in-zip filename. `undefined`
  // = not yet loaded; `null` = no bundle present / malformed (resolve live).
  private bundledIndex: Map<string, string> | null | undefined = undefined;

  // Packages the primary CDN dropped that we route through the esm.sh fallback
  // (name → requested range). Consulted in `_fetchModule` to synthesize the
  // bridge package instead of fetching from the primary CDN.
  private esmFallbacks: Map<string, string> = new Map();

  bundler: Bundler;

  // esm.sh fallback for primary-CDN-dropped packages, ENABLED by default. It
  // fetches the package's esm.sh source with the resolved deps externalized and
  // transpiles it through the bundler's own chain, so its `require("react")`
  // binds to the APP's React — sharing the one instance (see esm-fallback.ts).
  // This fixes the dual-React render crash the earlier native-import approach hit
  // (lucide-react@1.x icons read `IconContext` via `useContext`). Only fires when
  // the primary CDN actually drops a package; on failure (esm.sh down, a
  // multi-chunk package) it throws the clear `assertDependenciesResolved`-style
  // error. Pass `null` to disable, or a stub fetcher in tests.
  //
  // The SUBPATH loader materializes deep imports of a fallback package
  // (`sql.js/dist/sql-asm.js`) — the synthesis covers only the package root, so
  // the subpath rail fetches the raw tarball file from unpkg. `null` disables
  // the rail (subpath imports keep the resolution error).
  //
  // With the esm.sh fallback ENABLED, the registry also runs the dist-declared
  // transitive rescue (R3-1203, see undeclared-transitive.ts): a package whose
  // dist imports a name no declared dependency resolves is fetched through the
  // same fallback. `null` (the fallback off) disables the rescue with it — the
  // pre-R3-1203 behaviour, the boot dies on the first such import.
  constructor(
    bundler: Bundler,
    private esmFallbackFetcher: EsmFallbackFetcher | null = nativeEsmFallbackFetcher,
    private esmFallbackSubpathLoader: EsmFallbackSubpathLoader | null = nativeEsmFallbackSubpathLoader,
  ) {
    this.bundler = bundler;
  }

  // Load the bundled-package index once from the mounted zip. Never throws: a
  // missing/unreadable/malformed index just means "no bundled packages" and every
  // dependency resolves live from the CDN exactly as before.
  private async ensureBundledIndex(): Promise<Map<string, string> | null> {
    if (this.bundledIndex !== undefined) return this.bundledIndex;
    try {
      this.bundledIndex = parseBundledIndex(await this.bundler.fs.readFileAsync(bundledIndexPath()));
    } catch {
      this.bundledIndex = null;
    }
    return this.bundledIndex;
  }

  // Return the bundled `ICDNModule` for a package if one is present in the zip,
  // else null (→ live CDN fetch). A read/decode failure falls back to the CDN too,
  // so a corrupt bundle degrades to the old path rather than breaking the boot.
  private async _fetchBundledModule(name: string, version: string): Promise<ICDNModule | null> {
    const relPath = (await this.ensureBundledIndex())?.get(`${name}@${version}`);
    if (!relPath) return null;
    try {
      const module = decodeBundledModule(await this.bundler.fs.readBytesAsync(bundledPackagePath(relPath)));
      logger.debug('using bundled package (R3-49a), skipping CDN fetch', name, version);
      return module;
    } catch (err) {
      logger.warn('bundled package read failed; falling back to CDN', name, version, err);
      return null;
    }
  }

  async fetchManifest(
    deps: DepMap,
    shouldFilterBuildDeps = true,
    lockset?: LocksetSection,
    echo?: LocksetEchoContext,
  ): Promise<void> {
    if (shouldFilterBuildDeps) {
      deps = filterBuildDeps(deps);
    }

    const sortedDeps = sortObj(deps);

    // A sidecar lockset (PRETRANSPILED_ARTIFACTS_SPEC §5.4) replaces the
    // blocking /dep_tree request — but only on an exact input match, so a
    // stale or foreign lockset can never be applied. `validateLockset` has
    // already checked shape + cdnVersion; the dependency echo is checked here
    // because this is where the final (filtered) input DepMap exists.
    // R3-844: with an echo context (a git-library consumer), the match runs
    // against the PRE-strip map the CLI mirrors — see `depMapsEchoMatch`.
    let resolvedFromLockset = false;
    if (lockset) {
      if (depMapsEchoMatch(sortedDeps, lockset, echo)) {
        // The echo matches the INPUT, but a lockset could still inject extra
        // packages into `resolved` (SPEC_REVIEW PT-2). Reject the WHOLE lockset
        // if its resolved set isn't closed over the declared deps; never trust
        // it partially. Falls through to live /dep_tree resolution.
        if (locksetClosureValid(lockset)) {
          // A lockset carrying a prerelease nobody asked for is rejected whole
          // too (R3-600): it was built against the same prerelease-happy CDN
          // the live path now guards, so applying it would freeze the canary
          // into the boot. Live resolution re-pins and retries instead.
          if (findUnrequestedPrereleases(sortedDeps, lockset.resolved).length === 0) {
            logger.debug('Using sidecar lockset, skipping dep_tree resolution', lockset.resolved);
            // R3-844: git-form names resolve from the MOUNT, never the lockset
            // (the runtime stripped them from its own input; a resolved entry
            // under one — the CLI's gap-filler can produce one — would have
            // preloadModules fetch a package the mount already provides). A git
            // name the preset's AUGMENTATION re-introduced into the runtime's
            // input (an app git-pinning core-js, say) is KEPT: the runtime still
            // requests it, and dropping it would fail the completeness assert
            // below on a package.json that boots fine live.
            this.manifest = echo?.gitNames.size
              ? lockset.resolved.filter((r) => !(echo.gitNames.has(r.n) && !(r.n in sortedDeps)))
              : lockset.resolved;
            resolvedFromLockset = true;
          } else {
            logger.warn('Sidecar lockset carries a prerelease no requested range asked for; resolving live');
          }
        } else {
          logger.warn(
            'Sidecar lockset failed closure validation (resolved not closed over declared deps); resolving live',
          );
        }
      } else {
        logger.debug('Sidecar lockset dependency echo does not match; resolving live', {
          // R3-844: with an echo context the comparator's left-hand side is the
          // PRE-strip map, not the runtime's fetch input — log both, labeled,
          // or a git-library mismatch would print a map that was never compared.
          compared: echo?.echoMap ?? sortedDeps,
          fetchInput: sortedDeps,
          lockset: lockset.dependencies,
        });
      }
    }

    if (!resolvedFromLockset) {
      logger.debug('Fetching manifest', sortedDeps);
      this.manifest = await this.resolveManifestGuarded(sortedDeps);
      logger.debug('fetched manifest', this.manifest);
    }

    // Completeness applies to BOTH paths. A package the primary CDN drops is
    // missing whether resolved live OR baked into a sidecar lockset — the CLI
    // builds the lockset against the SAME CDN (PRETRANSPILED_ARTIFACTS_SPEC
    // §5.4), so a build-time drop is frozen into `lockset.resolved` and
    // `locksetClosureValid` (which only rejects INJECTED top-level packages, not
    // MISSING ones) won't catch it. Register esm.sh fallbacks (no-op when
    // disabled) then guard, so a dropped dep surfaces the clear error rather than
    // a cryptic undefined import — on either path.
    this.registerEsmFallbacks(sortedDeps);
    assertDependenciesResolved(sortedDeps, this.manifest);
  }

  /**
   * Resolve through the primary CDN with the unrequested-prerelease guard
   * (R3-600): the CDN has answered caret ranges with canaries (`react
   * ^19.2.5` → `19.3.0-canary-…` while stable 19.3.0 was on npm), and under
   * npm's semver a caret NEVER matches a prerelease. On offending entries,
   * top-level dependencies are re-resolved pinned to their range's floor
   * (`^19.2.5` → `19.2.5`) — ONE retry, with a console warning naming the
   * package, its range, the refused version and the pinned one. A range that
   * cannot reduce to a floor (`*`, `latest`, a multi-range) is not re-pinned:
   * that dependency fails loud. A prerelease that survives the retry fails the
   * boot on the same surface `assertDependenciesResolved` uses.
   */
  private async resolveManifestGuarded(deps: DepMap): Promise<IResolvedDependency[]> {
    let manifest = await fetchManifest(deps);
    const offending = findUnrequestedPrereleases(deps, manifest);
    if (offending.length === 0) return manifest;

    const retry: DepMap = { ...deps };
    for (const entry of offending) {
      const range = entry.range ?? deps[entry.n];
      if (range === undefined) continue; // transitive: nothing this run asked for — see below
      const floor = concreteVersion(range);
      if (floor === undefined) {
        throw new Error(
          `The package CDN resolved "${entry.n}" to the prerelease ${entry.v}, and the requested ` +
            `range "${range}" has no concrete version to re-pin it to. Pin an exact version ` +
            `in package.json.`,
        );
      }
      logger.warn(
        `The package CDN resolved "${entry.n}@${range}" to the prerelease ${entry.v}; ` +
          `re-resolving pinned at ${floor}`,
      );
      retry[entry.n] = floor;
    }
    manifest = await fetchManifest(retry);
    const remaining = findUnrequestedPrereleases(retry, manifest);
    if (remaining.length > 0) {
      const list = remaining.map((e) => `"${e.n}"→${e.v}`).join(', ');
      throw new Error(
        `The package CDN resolved prereleases no requested range asked for (${list}), and they ` +
          `survived a re-resolution pinned to the requested floors. Refusing to boot a prerelease ` +
          `nobody asked for — pin an exact version in package.json, or refresh the CDN's mirror.`,
      );
    }
    return manifest;
  }

  // For every requested top-level dep the primary CDN silently dropped, register
  // an esm.sh fallback and append a manifest entry so `preloadModules` fetches
  // it. No-op when the fallback is disabled (`esmFallbackLoader === null`).
  private registerEsmFallbacks(requested: DepMap): void {
    if (!this.esmFallbackFetcher) return;
    const resolved = new Set(this.manifest.map((dep) => dep.n));
    for (const [name, range] of Object.entries(requested)) {
      if (resolved.has(name)) continue;
      this.esmFallbacks.set(name, range);
      this.manifest.push({ n: name, v: range, d: 0 });
      logger.warn(`Primary CDN did not resolve "${name}@${range}"; routing through the esm.sh fallback`);
    }
  }

  async preloadModules(): Promise<void> {
    await Promise.all(
      this.manifest.map((dep) => {
        return this.fetchNodeModule(dep.n, dep.v);
      }),
    );
    await this.rescueUndeclaredImports();
  }

  /**
   * R3-1203 — reconcile the loaded packages' ACTUAL imports (their `m` list,
   * the CDN's package-level answer computed from the real dist) against what
   * the boot resolved, and fetch anything the whole chain left unnamed through
   * the esm.sh fallback: a dist-declared transitive the resolved version's
   * package.json does not declare (the recorded case:
   * `@codemirror/language@6.13.0` importing `@codemirror/streamparser`,
   * declared only from 6.13.1) is resolved at esm.sh's `*` (latest — no range
   * was ever declared for it) with the already-resolved deps externalized, and
   * registered like any fallback package. Runs to a fixpoint so a rescued
   * package's own bare imports get the same treatment.
   *
   * Never throws: a name the fallback cannot answer is warned about and left
   * alone, preserving the R3-411 bar (a guarded require in a file the app never
   * imports must not fail the boot — the module-not-found surfaces exactly if
   * the import is reached). The budget bounds the boot's fallback round-trips
   * against metadata naming unbounded unknowns.
   */
  private async rescueUndeclaredImports(): Promise<void> {
    if (!this.esmFallbackFetcher) return;
    let rescued = 0;
    const failed = new Set<string>();
    const unrescuedNames = (current: UndeclaredImport[]): string =>
      [...new Set([...failed, ...current.map((c) => c.name)])].join(', ');
    for (;;) {
      // What counts as resolved: the manifest, the self-hosted bases, every
      // registered package (so a package.json re-edit that re-runs the boot
      // does not re-rescue), and the registry-resolved names (git-form deps —
      // the mount provides them; a rescue would fetch an esm.sh duplicate).
      const known = new Set([
        ...this.bundler.registryResolvedNames(),
        ...this.manifest.map((dep) => dep.n),
        ...this.modules.keys(),
      ]);
      const candidates = collectUndeclaredImports(this.modules, known).filter((c) => !failed.has(c.name));
      if (candidates.length === 0) {
        if (failed.size > 0) {
          logger.warn(
            `Undeclared-import rescue could not resolve: ${[...failed].join(', ')}; ` +
              `left unresolved. These surface as module-not-found if actually imported.`,
          );
        }
        return;
      }
      if (rescued >= UNDECLARED_IMPORT_RESCUE_BUDGET) {
        logger.warn(
          `Undeclared-import rescue stopped at its ${UNDECLARED_IMPORT_RESCUE_BUDGET}-package budget; ` +
            `not resolved: ${unrescuedNames(candidates)}. These surface as module-not-found if actually imported.`,
        );
        return;
      }
      const batch = candidates.slice(0, UNDECLARED_IMPORT_RESCUE_BUDGET - rescued);
      const outcomes = await Promise.all(
        batch.map((candidate) =>
          this.rescueOneUndeclared(candidate).then((ok) => {
            if (!ok) failed.add(candidate.name);
            return ok;
          }),
        ),
      );
      const registered = outcomes.filter((outcome) => outcome);
      if (registered.length === 0) continue; // all of this batch failed — the next pass warns and stops
      rescued += registered.length;
    }
  }

  // Fetch one rescued package through the esm.sh fallback at `*`. The
  // synthesized NodeModule's `modules` (its `m` slot) is seeded with the
  // transpiled entry's bare specifiers so the fixpoint loop picks them up on
  // its next pass exactly like a CDN package's import list.
  private async rescueOneUndeclared(candidate: UndeclaredImport): Promise<boolean> {
    let mod: NodeModule;
    try {
      mod = await this._fetchEsmFallbackModule(candidate.name, '*');
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      logger.warn(
        `Could not rescue the undeclared import "${candidate.name}" through the esm.sh fallback ` +
          `(${reason}); leaving it unresolved`,
        { name: candidate.name },
      );
      return false;
    }
    const index = mod.files['index.js'];
    if (typeof index === 'object') mod.modules.push(...index.d);
    // Register exactly like a dropped top-level dependency: the manifest entry
    // (honest bookkeeping — the registry HAS resolved this name, depth ≥1 by
    // construction) and the esmFallbacks slot, so the SUBPATH rail
    // (`hasEsmFallback`-gated materialization) serves deep imports of the
    // rescued package too.
    this.esmFallbacks.set(candidate.name, '*');
    this.manifest.push({ n: candidate.name, v: '*', d: 1 });
    logger.warn(
      `"${candidate.importer}" imports "${candidate.name}", which no declared dependency resolves and the ` +
        `package CDN has not indexed; resolved ${candidate.name} at latest through the esm.sh fallback. ` +
        `Pin the package in package.json to make the dependency explicit.`,
    );
    return true;
  }

  private async _fetchModule(name: string, version: string): Promise<NodeModule> {
    const fallbackRange = this.esmFallbacks.get(name);
    if (fallbackRange !== undefined) {
      return this._fetchEsmFallbackModule(name, fallbackRange);
    }
    // Prefer the zip-bundled content (R3-49a); fall back to the live CDN fetch.
    const module = (await this._fetchBundledModule(name, version)) ?? (await fetchModule(name, version));
    const processedNodeModule = new NodeModule(name, version, module.f, module.m);
    this.modules.set(name, processedNodeModule);
    logger.debug('fetched module', name, version, module);
    return processedNodeModule;
  }

  // Load a primary-CDN-dropped package from esm.sh as a native ES module, stash
  // its namespace on the global for the shim to read, and register a synthetic
  // bridge package (see esm-fallback.ts). A loader failure (404, CSP block,
  // offline) surfaces as a clear error naming the package — never a silent
  // undefined import.
  private async _fetchEsmFallbackModule(name: string, range: string): Promise<NodeModule> {
    // Externalize every dependency the bundler can resolve by other means —
    // the CDN-resolved manifest deps and every self-hosted module (the SDK, via
    // `SELF_HOST_BASES`) — so esm.sh emits bare imports the bundler resolves to
    // the shared instances (esp. react/react-dom/sdk) instead of bundling
    // duplicates — see esm-fallback.ts. The self-hosted names are the piece that
    // kept the fallback inert for first-party packages: the SDK is deliberately
    // excluded from CDN resolution (it lags npm), so it never appears in the
    // manifest, and esm.sh was bundling its own SDK chunk — which the
    // single-module fallback then refused (R3-566).
    const externals = [...new Set([...this.manifest.map((d) => d.n), ...Object.keys(SELF_HOST_BASES)])].filter(
      (n) => n !== name,
    );
    const entryUrl = esmFallbackEntryUrl(name, range, externals);

    let source: string;
    try {
      source = await fetchEsmFallbackSource(entryUrl, this.esmFallbackFetcher!);
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      throw new Error(
        `Could not resolve "${name}@${range}" from the package CDN, and the esm.sh fallback (${entryUrl}) also failed: ${reason}`,
      );
    }

    // Transpile the esm.sh ES module through the SAME chain the bundler runs, so
    // its `require("react")` binds to the app's React, not a second instance.
    const transformed = await transformFile({ path: esmFallbackModulePath(name), code: source });
    if ('error' in transformed) {
      throw new Error(
        `Could not use the esm.sh fallback for "${name}@${range}": transpile failed: ${transformed.error.message}`,
      );
    }

    const module = synthesizeEsmFallbackModule(name, range, transformed.code, transformed.deps);
    const processedNodeModule = new NodeModule(name, range, module.f, module.m);
    this.modules.set(name, processedNodeModule);
    logger.debug('resolved module via esm.sh fallback (shared-React transpile)', name, range, entryUrl);
    return processedNodeModule;
  }

  /** Whether `name` is a package the primary CDN dropped (esm.sh-fallback). */
  hasEsmFallback(name: string): boolean {
    return this.esmFallbacks.has(name);
  }

  /** Test seam for the subpath rail — mirrors the bundler's `setRegistryFetcher`. */
  setEsmFallbackSubpathLoader(loader: EsmFallbackSubpathLoader | null): void {
    this.esmFallbackSubpathLoader = loader;
  }

  /** Test seam for the root fallback fetcher (same rationale as the loader seam). */
  setEsmFallbackFetcher(fetcher: EsmFallbackFetcher | null): void {
    this.esmFallbackFetcher = fetcher;
  }

  // In-flight subpath materializations, deduped per (name, subpath) so two
  // resolutions racing on the same deep import share one unpkg fetch.
  private esmFallbackSubpathPromises: Map<string, Promise<boolean>> = new Map();

  /**
   * Materialize a DEEP SUBPATH of an esm.sh-fallback package into its synthetic
   * module, so `name/sub` resolves the way the primary CDN would have served it.
   *
   * The root synthesis (`synthesizeEsmFallbackModule`) registers only
   * `package.json` + `index.js`, so an app importing `name/sub` (sql.js's
   * `dist/sql-asm.js`, the shape that killed sqlite-studio's live boot) fails
   * resolution: the file map has no such file and RegistryFS answers from it.
   * This fetches the RAW tarball file from unpkg (esm.sh's own subpath URL is a
   * multi-chunk stub the single-module fallback refuses), passes published CJS
   * through byte-for-byte with its require() graph scanned (the raw-cjs
   * convention) or transpiles ESM through the shared chain, then inlines the
   * file and precompiles its module — identical to what `loadModuleDependencies`
   * does for the files a CDN module inlines.
   *
   * Scope: SINGLE FILE per call — exactly the subpath the app imported. A
   * relative require INSIDE that file for a sibling the tarball also carries is
   * NOT materialized by this rail (it maps to the R3-411 stub that throws on
   * call), where a primary-CDN package would have its whole file map served.
   *
   * Returns TRUE when the subpath is resolvable after the call — including when
   * it was already materialized (the early return; nothing is fetched). Returns
   * FALSE when nothing was materialized: not a fallback package, the rail is
   * disabled, the module is missing from the registry, or the fetch/transpile
   * failed (a warning naming the package, subpath and cause is logged first) —
   * the caller keeps the original resolution error.
   */
  async ensureEsmFallbackSubpath(name: string, subpath: string): Promise<boolean> {
    if (!this.esmFallbacks.has(name) || !this.esmFallbackSubpathLoader) return false;
    const nodeModule = this.modules.get(name);
    if (!nodeModule) return false;
    if (nodeModule.files[subpath] != null) return true; // already materialized
    const key = `${name}\0${subpath}`;
    let promise = this.esmFallbackSubpathPromises.get(key);
    if (!promise) {
      promise = this._materializeEsmFallbackSubpath(name, subpath, nodeModule).finally(() =>
        this.esmFallbackSubpathPromises.delete(key),
      );
      this.esmFallbackSubpathPromises.set(key, promise);
    }
    return promise;
  }

  private async _materializeEsmFallbackSubpath(
    name: string,
    subpath: string,
    nodeModule: NodeModule,
  ): Promise<boolean> {
    const range = this.esmFallbacks.get(name)!;
    let code: string;
    try {
      code = await this.esmFallbackSubpathLoader!(name, range, subpath);
    } catch (err) {
      logger.warn(
        `esm.sh fallback subpath fetch failed for ${name}@${range}/${subpath}; the resolution error stands`,
        err,
      );
      return false;
    }
    const path = esmFallbackSubpathPath(name, subpath);
    let compiled: string;
    let deps: string[];
    const scan = scanCjsModule(code);
    if (scan.isEsm) {
      // ESM tarball file → the shared ESM→CJS chain (the CJS require-runtime
      // cannot evaluate import/export statements).
      const transformed = await transformFile({ path, code });
      if ('error' in transformed) {
        logger.warn(`esm.sh fallback subpath transpile failed for ${name}@${range}/${subpath}`, transformed.error);
        return false;
      }
      compiled = transformed.code;
      deps = transformed.deps;
    } else {
      // Published CJS is browser-ready as-is — the raw-cjs convention: the code
      // passes through byte-for-byte and only its require() graph is collected.
      compiled = code;
      deps = scan.requires;
    }
    const file: ICDNModuleFile = { c: compiled, d: deps, t: true };
    nodeModule.files[subpath] = file;
    const callbacks = this._writePrecompiledModule(path, file);
    await Promise.all(callbacks.map((fn) => fn()));
    return true;
  }

  async fetchNodeModule(name: string, version: string): Promise<NodeModule> {
    // Module already found, skip fetching
    // This could also check version, but for now this is fine
    // as we don't allow multiple versions of the same module right now
    const foundModule = this.modules.get(name);
    if (foundModule) {
      return foundModule;
    }

    const cacheKey = `${name}::${version}`;
    let promise = this.moduleDownloadPromises.get(cacheKey);
    if (!promise) {
      promise = this._fetchModule(name, version).finally(() => this.moduleDownloadPromises.delete(cacheKey));
      this.moduleDownloadPromises.set(cacheKey, promise);
    }
    return promise;
  }

  private _writePrecompiledModule(path: string, file: ICDNModuleFile): Array<() => Promise<void>> {
    if (this.bundler.modules.has(path)) {
      return [];
    }

    const module = new Module(path, file.c, true, this.bundler);
    this.bundler.modules.set(path, module);
    return file.d.map((dep) => {
      return async () => {
        // R3-411 — a precompiled package's dependency is resolved WITHOUT the
        // authority to fail the app: an unresolvable require (`node:child_process`
        // behind an `isNode` guard in a file the app never imports) maps to the
        // UNSUPPORTED-BUILTIN stub instead of throwing. The failure surfaces
        // exactly when the guarded path actually executes — the stub's exports
        // throw on call — which is the item's "fails only if that file is
        // imported" bar. Anything that is NOT a resolution miss still throws.
        try {
          await module.addDependency(dep);
        } catch (err) {
          if (!(err instanceof ModuleNotFoundError)) throw err;
          module.dependencyMap.set(dep, UNSUPPORTED_BUILTIN_MODULE_PATH);
          module.dependencies.add(UNSUPPORTED_BUILTIN_MODULE_PATH);
          this.bundler.addInitiator(UNSUPPORTED_BUILTIN_MODULE_PATH, module.id);
          return;
        }
        // Transform ONLY the dependency just added (its resolved path), not the whole
        // (growing) dependency set on every add — the latter is ~O(deps²) per module
        // (177k calls vs 3k for a real closure, measured). Each dep is still transformed
        // exactly once across the callbacks; transformModule is idempotent (precompiled
        // modules short-circuit on `compiled != null`).
        const resolved = module.dependencyMap.get(dep);
        if (resolved) {
          this.bundler.transformModule(resolved);
        }
      };
    });
  }

  async loadModuleDependencies() {
    const depPromises = [];
    for (let [moduleName, nodeModule] of this.modules) {
      for (let [fileName, file] of Object.entries(nodeModule.files)) {
        if (typeof file === 'object') {
          const promises = this._writePrecompiledModule(`/node_modules/${moduleName}/${fileName}`, file);
          depPromises.push(...promises);
        }
      }
    }
    await Promise.all(depPromises.map((fn) => fn()));
  }
}
